import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { LoginRequest } from '../../pages/auth/models/login-request.model';
import { AuthApi } from '../../pages/auth/services/auth.api';
import { ACCESS_TOKEN_REFRESH_BUFFER_MS } from './auth.constants';
import { mapLoginResponseToSession } from './auth-session.mapper';
import { AuthSession } from './auth-session.model';
import { SessionStore } from './session.store';
import { PORTAL_MODE } from './portal-mode';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly authApi = inject(AuthApi);
  private readonly sessionStore = inject(SessionStore);
  private readonly portalMode = inject(PORTAL_MODE);
  private refreshInFlight: Promise<boolean> | null = null;

  async login(payload: LoginRequest): Promise<AuthSession> {
    const loggedAt = new Date();
    const response = await firstValueFrom(this.authApi.login(payload));
    const session = mapLoginResponseToSession(response, payload.username, loggedAt);

    if (!this.isRoleAllowed(session)) {
      throw new Error('Account role is not allowed in this portal.');
    }

    this.sessionStore.setSession(session);
    return session;
  }

  async ensureValidSession(): Promise<boolean> {
    const session = this.sessionStore.snapshot();
    if (!session) {
      return false;
    }

    if (!this.isRoleAllowed(session)) {
      this.sessionStore.clear();
      return false;
    }

    if (this.hasValidAccessToken(session)) {
      return true;
    }

    return this.refreshSession(session);
  }

  logout(): void {
    this.sessionStore.clear();
  }

  async changePassword(password: string): Promise<void> {
    await firstValueFrom(this.authApi.changePassword(password));
    this.sessionStore.clear();
  }

  private hasValidAccessToken(session: AuthSession): boolean {
    const expiresAt = new Date(session.accessTokenExpiresAt).getTime();
    return Number.isFinite(expiresAt) && expiresAt - ACCESS_TOKEN_REFRESH_BUFFER_MS > Date.now();
  }

  private async refreshSession(session: AuthSession): Promise<boolean> {
    if (this.refreshInFlight) {
      return this.refreshInFlight;
    }

    this.refreshInFlight = this.performRefresh(session);

    try {
      return await this.refreshInFlight;
    } finally {
      this.refreshInFlight = null;
    }
  }

  private async performRefresh(session: AuthSession): Promise<boolean> {
    try {
      const refreshedAt = new Date();
      const response = await firstValueFrom(this.authApi.refresh(session.refreshToken));
      const nextSession = mapLoginResponseToSession(response, session.login, refreshedAt);

      if (!this.isRoleAllowed(nextSession)) {
        this.sessionStore.clear();
        return false;
      }

      this.sessionStore.setSession(nextSession);
      return true;
    } catch {
      this.sessionStore.clear();
      return false;
    }
  }

  private isRoleAllowed(session: AuthSession): boolean {
    return this.portalMode === 'platform'
      ? session.role === 'SUPER_ADMIN'
      : session.role !== 'SUPER_ADMIN';
  }
}
